import { Link } from "react-router-dom";
import { useI18n } from "../i18n/I18nContext";
import { EU_COUNTRIES, findCountry } from "../data/vatData";
import { ArrowRight } from "lucide-react";
import { trackPopularRoute } from "../lib/analytics";

// Curated "popular" routes — high-volume cross-border lanes for EU e-commerce.
// These render as SEO-friendly internal links, also helping the calculator surface common scenarios.
const POPULAR_PAIRS = [
    ["DE", "FR"], ["FR", "DE"], ["DE", "IT"], ["NL", "DE"],
    ["IE", "DE"], ["ES", "FR"], ["LV", "DE"], ["LT", "DE"],
    ["EE", "FI"], ["PL", "DE"], ["BE", "NL"], ["AT", "DE"],
];

export default function PopularRoutes() {
    const { t } = useI18n();
    const pairs = POPULAR_PAIRS
        .map(([f, b]) => ({ from: findCountry(f), to: findCountry(b) }))
        .filter((p) => p.from && p.to);

    return (
        <section
            data-testid="popular-routes-section"
            className="max-w-7xl mx-auto px-4 sm:px-6 py-12"
        >
            <div className="max-w-2xl mb-8">
                <div className="text-xs font-semibold tracking-[0.2em] uppercase text-slate-500">
                    {t("popular.eyebrow")}
                </div>
                <h2 className="font-display mt-3 text-2xl sm:text-3xl tracking-tight font-bold text-slate-900">
                    {t("popular.title")}
                </h2>
                <p className="mt-3 text-slate-600 leading-relaxed">{t("popular.intro")}</p>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {pairs.map(({ from, to }) => (
                    <Link
                        key={`${from.code}-${to.code}`}
                        to={`/vat/${from.slug}-to-${to.slug}`}
                        onClick={() => trackPopularRoute(from.code, to.code)}
                        data-testid={`popular-${from.code}-${to.code}`}
                        className="group rounded-lg border border-slate-200 bg-white p-4 hover:border-[#10B981] hover:shadow-sm transition-all"
                    >
                        <div className="flex items-center gap-2 text-sm font-semibold text-slate-900">
                            <span>{from.flag}</span>
                            <span className="truncate">{from.name}</span>
                            <ArrowRight className="h-3.5 w-3.5 text-slate-400 group-hover:text-[#10B981] transition-colors shrink-0" />
                            <span>{to.flag}</span>
                            <span className="truncate">{to.name}</span>
                        </div>
                        <div className="mt-1 text-xs text-slate-500">
                            {to.standard}% {t("popular.standard")}
                        </div>
                    </Link>
                ))}
            </div>

            <div className="mt-6 flex flex-wrap gap-x-2 gap-y-1 text-xs text-slate-500">
                <span className="font-semibold text-slate-700">{t("popular.allCountries")}:</span>
                {EU_COUNTRIES.map((c, i) => (
                    <span key={c.code}>
                        <Link
                            to={`/vat/germany-to-${c.slug}`}
                            className="hover:text-[#10B981] hover:underline"
                            data-testid={`country-link-${c.code}`}
                        >
                            {c.name}
                        </Link>
                        {i < EU_COUNTRIES.length - 1 && <span className="text-slate-300">·</span>}
                    </span>
                ))}
            </div>
        </section>
    );
}
